import { CamundaBaseService, isAuthError, isCamundaRequestError, isTaskNotActiveError } from './base-service';
import { CamundaUserService } from './user-service';
import type {
  CamundaFormValue,
  CamundaFormObject,
  CamundaGroup,
  CamundaTask,
  CamundaUser,
  CamundaValueInfo,
  HistoricProcessInstance,
  HistoricTaskInstance,
  IdentityLink,
  ProcessDefinition,
  TaskFormVariable,
  TaskFormVariables,
} from './types';
import {
  encodeCamundaPathSegment,
  getProcessDefinitionKeyFromId,
} from './process-definition-id';
import { z } from 'zod';

const TASK_DEEP_LINK_CREATED_BEFORE_SENT_AT_MS = 2 * 60 * 1000;
const TASK_DEEP_LINK_CREATED_AFTER_SENT_AT_MS = 30 * 60 * 1000;

const camundaFormValueSchema: z.ZodType<CamundaFormValue> = z.lazy(() => z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.undefined(),
  z.instanceof(File),
  z.instanceof(Blob),
  z.array(camundaFormValueSchema),
  z.record(z.string(), camundaFormValueSchema),
]));

const taskFormVariableSchema = z.object({
  value: z.unknown(),
  type: z.string().optional(),
  valueInfo: z.object({
    filename: z.string().optional(),
    mimeType: z.string().optional(),
    objectTypeName: z.string().optional(),
    serializationDataFormat: z.string().optional(),
  }).optional(),
});

const parseTaskFormVariable = <TValue>(payload: TValue): TaskFormVariable | null => {
  const parsedPayload = taskFormVariableSchema.safeParse(payload);
  if (!parsedPayload.success) {
    return null;
  }
  const parsedValue = camundaFormValueSchema.safeParse(parsedPayload.data.value);
  if (!parsedValue.success) {
    return null;
  }
  const valueInfo: CamundaValueInfo | undefined = parsedPayload.data.valueInfo;
  return {
    value: parsedValue.data,
    type: parsedPayload.data.type,
    valueInfo,
  };
};

const parseTaskFormVariables = <TValue extends object>(payload: TValue): TaskFormVariables => {
  const parsedVariables: TaskFormVariables = {};
  for (const [name, value] of Object.entries(payload)) {
    const parsedVariable = parseTaskFormVariable(value);
    if (parsedVariable) {
      parsedVariables[name] = parsedVariable;
    }
  }
  return parsedVariables;
};

export interface TaskDeepLinkResolveParams {
  processInstanceId: string;
  taskDefinitionKey: string;
  sentAt?: string | null;
}

export type TaskDeepLinkResolveResult =
  | { status: 'active'; task: CamundaTask }
  | { status: 'pending' }
  | { status: 'completed_or_ended' }
  | { status: 'forbidden' }
  | { status: 'ambiguous' }
  | { status: 'invalid_link' }
  | { status: 'error' };

export class CamundaTaskService extends CamundaBaseService {
  private static readonly processDefinitionInfoCache = new Map<
    string,
    { key: string; name: string }
  >();

  static async getAvailableTasksForUser(userId: string): Promise<CamundaTask[]> {
    const userGroupsPromise = CamundaUserService.getUserGroups(userId);
    const assigneeTasksPromise = this.makeRequest<CamundaTask[]>(
      this.buildTaskQueryEndpoint({ assignee: userId })
    );
    const candidateUserTasksPromise = this.makeRequest<CamundaTask[]>(
      this.buildTaskQueryEndpoint({
        candidateUser: userId,
        includeAssignedTasks: 'true',
      })
    );
    const candidateGroupTasksPromise = userGroupsPromise.then((userGroups) =>
      this.getCandidateGroupTasks(this.getValidGroupIds(userGroups))
    );

    const results = await Promise.all([
      assigneeTasksPromise,
      candidateUserTasksPromise,
      candidateGroupTasksPromise,
    ]);

    const allTasks = results
      .flat()
      .filter((task): task is CamundaTask => Boolean(task?.id));

    const finalTasks = this.normalizeTaskListItems(allTasks);
    
    return finalTasks;
  }

  static async getAvailableTaskForUserById(
    taskId: string,
    userId: string,
  ): Promise<CamundaTask | null> {
    const userGroupsPromise = CamundaUserService.getUserGroups(userId);
    const assigneeTasksPromise = this.makeRequest<CamundaTask[]>(
      this.buildTaskQueryEndpoint({ taskId, assignee: userId }),
    );
    const candidateUserTasksPromise = this.makeRequest<CamundaTask[]>(
      this.buildTaskQueryEndpoint({
        taskId,
        candidateUser: userId,
        includeAssignedTasks: 'true',
      }),
    );
    const candidateGroupTasksPromise = userGroupsPromise.then((userGroups) => {
      const groupIds = this.getValidGroupIds(userGroups);
      if (groupIds.length === 0) {
        return [];
      }

      return this.makeRequest<CamundaTask[]>(
        this.buildTaskQueryEndpoint({
          taskId,
          candidateGroups: groupIds.join(','),
          includeAssignedTasks: 'true',
        }),
      );
    });

    const results = await Promise.allSettled([
      assigneeTasksPromise,
      candidateUserTasksPromise,
      candidateGroupTasksPromise,
    ]);
    const tasks = results.flatMap((result) =>
      result.status === 'fulfilled'
        ? result.value.filter(
            (task): task is CamundaTask =>
              task != null &&
              task.id === taskId &&
              (!task.assignee || task.assignee === userId),
          )
        : [],
    );

    const availableTask = tasks[0];
    if (availableTask) {
      return this.enrichTaskDetails(availableTask);
    }

    const failedResult = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );
    if (failedResult) {
      throw failedResult.reason;
    }
    return null;
  }

  static async getPendingTasks(): Promise<CamundaTask[]> {
    const userId = CamundaUserService.getCurrentUserId();
    if (!userId) throw new Error('No hay usuario autenticado');
    return this.getAvailableTasksForUser(userId);
  }

  static async getTaskCountForUser(userId: string): Promise<number> {
    try {
      const userGroups = await CamundaUserService.getUserGroups(userId);
      const groupIds = userGroups
        .map((g) => g?.id)
        .filter((id): id is string => id.length > 0);
      
      const countQueries = [
        this.makeRequest<{count: number}>(`/task/count?assignee=${userId}`),
        this.makeRequest<{count: number}>(`/task/count?candidateUser=${userId}&includeAssignedTasks=true`)
      ];
      
      for (const groupId of groupIds) {
        countQueries.push(
          this.makeRequest<{count: number}>(`/task/count?candidateGroup=${groupId}&includeAssignedTasks=true`)
        );
      }
      
      const results = await Promise.all(countQueries);
      
      const counts = results.map((result) => result.count);
      return counts.length > 0 ? Math.max(...counts, 0) : 0;
    } catch (error) {
      console.error('Error getting task count for user:', error);
      return 0;
    }
  }

  static async getMyTaskCount(userId: string): Promise<number> {
    try {
      const result = await this.makeRequest<{count: number}>(`/task/count?assignee=${userId}`);
      return result.count;
    } catch (error) {
      console.error('Error getting my task count:', error);
      return 0;
    }
  }

  static async getTaskDetails(taskId: string): Promise<CamundaTask | null> {
    try {
      const task = await this.makeRequest<CamundaTask>(
        `/task/${taskId}`,
        { suppressErrorLogStatuses: [404] },
      );
      return this.enrichTaskDetails(task);
    } catch (error) {
      if (!isTaskNotActiveError(error)) {
        throw error;
      }
      return null;
    }
  }

  static async resolveTaskDeepLink(
    params: TaskDeepLinkResolveParams
  ): Promise<TaskDeepLinkResolveResult> {
    const processInstanceId = params.processInstanceId.trim();
    const taskDefinitionKey = params.taskDefinitionKey.trim();
    if (!processInstanceId || !taskDefinitionKey) {
      return { status: 'invalid_link' };
    }

    const sentAtMs = this.parseDeepLinkSentAt(params.sentAt);
    if (params.sentAt && sentAtMs === null) {
      return { status: 'invalid_link' };
    }

    const query = new URLSearchParams({
      processInstanceId,
      taskDefinitionKey,
      sortBy: 'created',
      sortOrder: 'desc',
    });

    let activeTasks: CamundaTask[];
    try {
      activeTasks = await this.makeRequest<CamundaTask[]>(`/task?${query}`, {
        suppressErrorLogStatuses: [403],
      });
    } catch (error) {
      return this.classifyDeepLinkError(error);
    }

    const matchingTasks = activeTasks.filter((task) =>
      task.processInstanceId === processInstanceId &&
      task.taskDefinitionKey === taskDefinitionKey &&
      this.isTaskCreatedForDeepLink(task.created, sentAtMs)
    );

    if (matchingTasks.length === 1) {
      const [task] = this.normalizeTaskListItems(matchingTasks);
      return task ? { status: 'active', task } : { status: 'error' };
    }

    if (matchingTasks.length > 1) {
      return { status: 'ambiguous' };
    }

    if (sentAtMs !== null && activeTasks.length > 0) {
      return { status: 'completed_or_ended' };
    }

    return this.resolveEmptyDeepLinkMatch(processInstanceId, taskDefinitionKey, sentAtMs);
  }

  static async isTaskRelevantForUser(taskId: string, userId: string): Promise<boolean> {
    try {
      const task = await this.getTaskDetails(taskId);
      if (!task) return false;

      if (task.assignee === userId) {
        return true;
      }

      const identityLinks = await this.makeRequest<IdentityLink[]>(`/task/${taskId}/identity-links?type=candidate`);
      if (identityLinks.some(link => link.userId === userId)) {
        return true;
      }

      const userGroups = await CamundaUserService.getUserGroups(userId);
      const groupIds = new Set(userGroups.map(g => g.id));
      
      return identityLinks.some(link => link.groupId && groupIds.has(link.groupId));

    } catch (error) {
      console.error(`Error checking task relevance for user ${userId} on task ${taskId}:`, error);
      return false;
    }
  }

  static async claimTask(taskId: string, userId: string): Promise<boolean> {
    return this.makeRequestNoContent(`/task/${taskId}/claim`, {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
  }

  static async unclaimTask(taskId: string): Promise<boolean> {
    return this.makeRequestNoContent(`/task/${taskId}/unclaim`, { method: 'POST' });
  }

  static async completeTask(taskId: string, variables?: TaskFormVariables): Promise<boolean> {
    return this.makeRequestNoContent(`/task/${taskId}/complete`, {
      method: 'POST', body: JSON.stringify({ variables: variables ?? {} })
    });
  }

  static async reassignTask(taskId: string, newAssigneeId: string): Promise<boolean> {
    return this.makeRequestNoContent(`/task/${taskId}/assignee`, {
      method: 'POST', body: JSON.stringify({ userId: newAssigneeId })
    });
  }

  static async getTaskCandidateUsers(taskId: string): Promise<CamundaUser[]> {
    try {
      const candidateUsers = await this.makeRequest<IdentityLink[]>(`/task/${taskId}/identity-links?type=candidate`);
      const users: CamundaUser[] = [];
      const userIds = new Set<string>();
      
      for (const link of candidateUsers) {
        if (link.userId) {
          try {
            const user = await this.makeRequest<CamundaUser>(`/user/${link.userId}/profile`);
            if (!userIds.has(user.id)) {
              users.push(user);
              userIds.add(user.id);
            }
          } catch (error) {
            console.warn(`Error fetching user ${link.userId}:`, error);
          }
        }
      }
      
      const candidateGroups = candidateUsers.filter(link => link.groupId);
      for (const groupLink of candidateGroups) {
        try {
          const groupMembers = await this.makeRequest<CamundaUser[]>(`/user?memberOfGroup=${groupLink.groupId}`);
          for (const member of groupMembers) {
            if (!userIds.has(member.id)) {
              users.push(member);
              userIds.add(member.id);
            }
          }
        } catch (error) {
          console.warn(`Error fetching group members for ${groupLink.groupId}:`, error);
        }
      }
      
      return users.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.id;
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.id;
        return nameA.localeCompare(nameB);
      });
    } catch (error) {
      console.error('Error fetching task candidate users:', error);
      return [];
    }
  }

  private static buildTaskQueryEndpoint(params: Record<string, string>): string {
    const query = new URLSearchParams({
      ...params,
      sortBy: 'created',
      sortOrder: 'desc',
    });

    return `/task?${query}`;
  }

  private static async resolveEmptyDeepLinkMatch(
    processInstanceId: string,
    taskDefinitionKey: string,
    sentAtMs: number | null
  ): Promise<TaskDeepLinkResolveResult> {
    const historicTaskQuery = new URLSearchParams({
      processInstanceId,
      taskDefinitionKey,
      finished: 'true',
      sortBy: 'endTime',
      sortOrder: 'desc',
      maxResults: '10',
    });

    try {
      const historicTasks = await this.makeRequest<HistoricTaskInstance[]>(
        `/history/task?${historicTaskQuery}`,
        { suppressErrorLogStatuses: [403] },
      );
      const finishedTaskFromLink = historicTasks.some((task) =>
        task.taskDefinitionKey === taskDefinitionKey &&
        task.processInstanceId === processInstanceId &&
        this.isTaskCreatedForDeepLink(task.startTime, sentAtMs)
      );
      if (finishedTaskFromLink) {
        return { status: 'completed_or_ended' };
      }
    } catch (error) {
      const classified = this.classifyDeepLinkError(error);
      if (classified.status === 'forbidden') {
        return classified;
      }
    }

    const encodedProcessInstanceId = encodeCamundaPathSegment(processInstanceId);
    try {
      await this.makeRequest(`/process-instance/${encodedProcessInstanceId}`, {
        suppressErrorLogStatuses: [403, 404],
      });
      return { status: 'pending' };
    } catch (error) {
      if (isCamundaRequestError(error) && error.status === 404) {
        return this.resolveHistoricProcessDeepLinkState(encodedProcessInstanceId);
      }
      return this.classifyDeepLinkError(error);
    }
  }

  private static async resolveHistoricProcessDeepLinkState(
    encodedProcessInstanceId: string
  ): Promise<TaskDeepLinkResolveResult> {
    try {
      const historicProcess = await this.makeRequest<HistoricProcessInstance>(
        `/history/process-instance/${encodedProcessInstanceId}`,
        { suppressErrorLogStatuses: [403, 404] },
      );

      if (historicProcess.endTime || historicProcess.deleteReason || historicProcess.state === 'COMPLETED') {
        return { status: 'completed_or_ended' };
      }

      return { status: 'pending' };
    } catch (error) {
      if (isCamundaRequestError(error) && error.status === 404) {
        return { status: 'completed_or_ended' };
      }
      return this.classifyDeepLinkError(error);
    }
  }

  private static parseDeepLinkSentAt(sentAt?: string | null): number | null {
    if (!sentAt) {
      return null;
    }

    const parsed = new Date(sentAt).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }

  private static isTaskCreatedForDeepLink(createdAt: string | null | undefined, sentAtMs: number | null): boolean {
    if (sentAtMs === null) {
      return true;
    }

    if (!createdAt) {
      return false;
    }

    const createdAtMs = new Date(createdAt).getTime();
    if (!Number.isFinite(createdAtMs)) {
      return false;
    }

    return createdAtMs >= sentAtMs - TASK_DEEP_LINK_CREATED_BEFORE_SENT_AT_MS &&
      createdAtMs <= sentAtMs + TASK_DEEP_LINK_CREATED_AFTER_SENT_AT_MS;
  }

  private static classifyDeepLinkError<TError>(error: TError): TaskDeepLinkResolveResult {
    if (isAuthError(error)) {
      return { status: 'forbidden' };
    }

    if (isCamundaRequestError(error)) {
      return { status: 'error' };
    }

    return { status: 'error' };
  }

  private static getValidGroupIds(userGroups: CamundaGroup[]): string[] {
    const groupIds = new Set<string>();
    for (const group of userGroups) {
      if (group.id.length > 0) {
        groupIds.add(group.id);
      }
    }
    return [...groupIds];
  }

  private static async getCandidateGroupTasks(groupIds: string[]): Promise<CamundaTask[]> {
    if (groupIds.length === 0) {
      return [];
    }

    try {
      const tasks = await this.makeRequest<CamundaTask[]>(
        this.buildTaskQueryEndpoint({
          candidateGroups: groupIds.join(','),
          includeAssignedTasks: 'true',
        })
      );

      if (!Array.isArray(tasks)) {
        throw new Error('Camunda candidateGroups query returned a non-array response');
      }

      return tasks;
    } catch (error) {
      if (isAuthError(error)) {
        throw error;
      }

      console.warn(
        'Camunda candidateGroups task query failed; falling back to per-group queries:',
        error
      );
      const results = await Promise.all(
        groupIds.map((groupId) =>
          this.makeRequest<CamundaTask[]>(
            this.buildTaskQueryEndpoint({
              candidateGroup: groupId,
              includeAssignedTasks: 'true',
            })
          )
        )
      );

      return results.flatMap((tasks) => (Array.isArray(tasks) ? tasks : []));
    }
  }

  private static normalizeTaskListItems(tasks: CamundaTask[]): CamundaTask[] {
    const seenTaskIds = new Set<string>();
    const uniqueTasks: CamundaTask[] = [];

    for (const task of tasks) {
      if (!task?.id || seenTaskIds.has(task.id)) {
        continue;
      }
      seenTaskIds.add(task.id);
      uniqueTasks.push(task);
    }

    return uniqueTasks
      .map((task) => {
        const processDefinitionInfo = this.getProcessDefinitionInfoFromTask(task);
        const processDefinitionKey = processDefinitionInfo?.key || task.processDefinitionKey || '';
        const processDefinitionName =
          task.processDefinitionName ||
          processDefinitionInfo?.name ||
          processDefinitionKey ||
          'Proceso desconocido';

        return {
          ...task,
          processInstanceId: task.processInstanceId || '',
          processDefinitionKey,
          processDefinitionName,
          assignee_fullname: task.assignee_fullname ?? task.assignee,
        };
      })
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
  }

  private static async enrichTaskDetails(task: CamundaTask): Promise<CamundaTask> {
    const processInstanceId = await this.resolveProcessInstanceId(task);
    const processDefinitionInfo = await this.getProcessDefinitionInfo({ ...task, processInstanceId });

    let assignee_fullname: string | null = null;
    if (task.assignee) {
      try {
        const user = await CamundaUserService.getUserById(task.assignee);
        if (user) {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          assignee_fullname = fullName || user.id;
        }
      } catch (error) {
        console.warn(`Error fetching user details for ${task.assignee}:`, error);
        assignee_fullname = task.assignee;
      }
    }

    return {
      ...task,
      processInstanceId,
      processDefinitionKey: processDefinitionInfo.key,
      processDefinitionName: processDefinitionInfo.name,
      assignee_fullname,
    };
  }

  private static async resolveProcessInstanceId(
    task: Pick<CamundaTask, 'id' | 'processInstanceId'>
  ): Promise<string> {
    if (task.processInstanceId && task.processInstanceId !== task.id) {
      return task.processInstanceId;
    }

    try {
      const historicTasks = await this.makeRequest<HistoricTaskInstance[]>(
        `/history/task?taskId=${task.id}&maxResults=1`
      );
      return historicTasks[0]?.processInstanceId || '';
    } catch {
      return '';
    }
  }

  private static async getProcessDefinitionInfo(
    task: Pick<CamundaTask, 'id' | 'processDefinitionId' | 'processDefinitionKey' | 'processDefinitionName' | 'processInstanceId'>
  ): Promise<{ key: string; name: string }> {
    const cached = this.processDefinitionInfoCache.get(task.processDefinitionId);
    if (cached) {
      return cached;
    }

    const fallbackInfo = this.getProcessDefinitionInfoFromTask(task);
    if (fallbackInfo && task.processDefinitionName) {
      const info = { ...fallbackInfo, name: task.processDefinitionName };
      this.processDefinitionInfoCache.set(task.processDefinitionId, info);
      return info;
    }

    const historyInfo = await this.getProcessDefinitionInfoFromHistory(task);
    if (historyInfo) {
      this.processDefinitionInfoCache.set(task.processDefinitionId, historyInfo);
      return historyInfo;
    }

    try {
      const encodedProcessDefinitionId = encodeCamundaPathSegment(task.processDefinitionId);
      const process = await this.makeRequest<ProcessDefinition>(
        `/process-definition/${encodedProcessDefinitionId}`,
        { suppressErrorLogStatuses: [404] },
      );
      const info = {
        key: process.key || '',
        name: process.name || process.key || 'Proceso sin nombre',
      };
      this.processDefinitionInfoCache.set(task.processDefinitionId, info);
      return info;
    } catch {
      if (fallbackInfo) {
        this.processDefinitionInfoCache.set(task.processDefinitionId, fallbackInfo);
        return fallbackInfo;
      }

      return {
        key: '',
        name: 'Proceso desconocido',
      };
    }
  }

  private static async getProcessDefinitionInfoFromHistory(
    task: Pick<CamundaTask, 'id' | 'processInstanceId'>
  ): Promise<{ key: string; name: string } | null> {
    if (task.processInstanceId) {
      try {
        const process = await this.makeRequest<HistoricProcessInstance>(
          `/history/process-instance/${task.processInstanceId}`,
          { suppressErrorLogStatuses: [404] },
        );
        if (process.processDefinitionKey) {
          return {
            key: process.processDefinitionKey,
            name: process.processDefinitionName || process.processDefinitionKey,
          };
        }
      } catch {
      }
    }

    try {
      const historicTasks = await this.makeRequest<HistoricTaskInstance[]>(
        `/history/task?taskId=${task.id}&maxResults=1`
      );
      const historicTask = historicTasks[0];
      if (historicTask?.processDefinitionKey) {
        return {
          key: historicTask.processDefinitionKey,
          name: historicTask.processDefinitionKey,
        };
      }
    } catch {
    }

    return null;
  }

  private static getProcessDefinitionInfoFromTask(
    task: Pick<CamundaTask, 'processDefinitionId' | 'processDefinitionKey' | 'processDefinitionName'>
  ): { key: string; name: string } | null {
    const key =
      task.processDefinitionKey?.trim() ||
      (task.processDefinitionId.includes(':')
        ? getProcessDefinitionKeyFromId(task.processDefinitionId)
        : null);

    if (!key) {
      return null;
    }

    return {
      key,
      name: task.processDefinitionName || key,
    };
  }

  static async getTaskVariable(taskId: string, varName: string): Promise<TaskFormVariable | null> {
    try {
      const payload = await this.makeRequest<unknown>(`/task/${taskId}/variables/${varName}`);
      return parseTaskFormVariable(payload);
    } catch {
      return null;
    }
  }

  static async getMultipleTaskVariables(taskId: string, varNames: string[]): Promise<TaskFormVariables> {
    if (varNames.length === 0) return {};
    
    try {
      const allVariables = parseTaskFormVariables(
        await this.makeRequest<CamundaFormObject>(`/task/${taskId}/variables`),
      );

      const requestedVariables: TaskFormVariables = {};
      
      const normalVars = varNames.filter(name => !name.endsWith('_label'));
      const labelVars = varNames.filter(name => name.endsWith('_label'));
      
      normalVars.forEach(varName => {
        if (allVariables[varName]) {
          requestedVariables[varName] = allVariables[varName];
        }
      });
      
      labelVars.forEach(labelVar => {
        const baseVar = labelVar.replace('_label', '');
        if (requestedVariables[baseVar] && allVariables[labelVar]) {
          requestedVariables[labelVar] = allVariables[labelVar];
        }
      });
      
      return requestedVariables;
    } catch {
      return {};
    }
  }

  static async getParticipatingProcessInstanceIds(userId: string): Promise<string[]> {
    const tasks = await this.getAvailableTasksForUser(userId);
    const processInstanceIds = new Set<string>();
    for (const task of tasks) {
      if (task.processInstanceId.length > 0) {
        processInstanceIds.add(task.processInstanceId);
      }
    }
    return [...processInstanceIds];
  }

  static async getRuntimeTasksForProcessInstance(
    processInstanceId: string,
  ): Promise<CamundaTask[]> {
    try {
      const tasks = await this.makeRequest<CamundaTask[]>(
        `/task?processInstanceId=${encodeCamundaPathSegment(processInstanceId)}&sortBy=created&sortOrder=asc`,
      );
      return Array.isArray(tasks) ? tasks : [];
    } catch {
      return [];
    }
  }

  static async getUserParticipatingRuntimeTasksForProcess(
    processInstanceId: string,
    userId: string,
  ): Promise<CamundaTask[]> {
    const tasks = await this.getRuntimeTasksForProcessInstance(processInstanceId);
    const relevant: CamundaTask[] = [];
    for (const task of tasks) {
      if (await this.isTaskRelevantForUser(task.id, userId)) {
        relevant.push(task);
      }
    }
    return relevant;
  }

  static mapRuntimeTaskToHistoric(task: CamundaTask): HistoricTaskInstance {
    return {
      id: task.id,
      processDefinitionKey: task.processDefinitionKey ?? null,
      processDefinitionId: task.processDefinitionId ?? null,
      processInstanceId: task.processInstanceId ?? null,
      executionId: null,
      caseDefinitionKey: null,
      caseDefinitionId: null,
      caseInstanceId: null,
      caseExecutionId: null,
      activityInstanceId: '',
      name: task.name ?? null,
      description: null,
      deleteReason: null,
      owner: null,
      assignee: task.assignee ?? null,
      startTime: task.created,
      endTime: null,
      duration: null,
      taskDefinitionKey: task.taskDefinitionKey,
      priority: task.priority,
      due: null,
      parentTaskId: null,
      followUp: null,
      tenantId: null,
      removalTime: null,
      rootProcessInstanceId: task.processInstanceId ?? null,
      taskState: 'Created',
    };
  }
}
